import { HttpParams } from '@angular/common/http';
import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output, Optional } from '@angular/core';
import {
    LISTE_ABSCHLIESSEN_STEPPER_HANDLER,
    ListeAbschliessenStepperHandler,
} from '../liste-abschliessen-stepper.handler';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { ConfirmDialogComponent } from '../../../dialogs/confirm-dialog/confirm-dialog.component';
import { EnterValueDialogComponent } from '../../../dialogs/enter-value-dialog/enter-value-dialog.component';
import { EnterValueInput, EnterValueResponse } from '../../../dialogs/enter-value-dialog/enter-value-response';
import { GruppeTier } from '../../../gruppe-tier/classes/gruppe-tier';
import { API_BASE_URL_SERVICE } from '../../../../kiene-core.config';
import { KieneBackendApiService } from '../../../../services/backend-api/kiene-backend-api.service';
import { MessageService } from '../../../../services/message-service/message.service';
import { KieneUtils } from '../../../../utils/KieneUtils';
import { GruppenService } from '../../gruppen.service';
import { AbgangslisteSchliessenDialogComponent } from '../abgangsliste-schliessen-dialog/abgangsliste-schliessen-dialog.component';
import { GruppeAbgangService } from '../gruppe-abgang.service';
import { Tierliste } from '../../../gruppe-tier/classes/tierliste';
import { AppService } from '../../../../services/app/app.service';
import { ConfirmDialogInput, ConfirmDialogResult } from '../../../dialogs/confirm-dialog/confirm-dialog';
import { LocalStorageService } from '../../../../services/local-storage/local-storage.service';

@Component({
    selector: 'kiene-abgang-liste',
    templateUrl: './abgang-liste.component.html',
    styleUrls: ['./abgang-liste.component.scss'],
})
export class AbgangListeComponent implements OnInit, OnChanges {
    protected readonly KieneUtils = KieneUtils;

    public tiere: GruppeTier[] = [];
    gefilterteTiere: GruppeTier[] = [];
    tierliste: Tierliste;
    ladeTiereFuerAbgangslisteSub: Subscription;
    private ladeAnzahlTiereZurListeSub?: Subscription;
    /** Lebende Tiere der Abgangsgruppe, die noch auf keiner Ausstallliste stehen. */
    anzahlTiereZurListeHinzufuegbar: number | null = null;
    isLoading = false;
    suchfeldCtrl = new UntypedFormControl('');

    @Input('alsDialog') alsDialog = false;
    @Input('anzahlTiereInGruppe') anzahlTiereInGruppe: number = 0;
    /** Mobile header shows the close-list action instead. */
    @Input() hideListeAbschliessenButton = false;
    @Output('tiereAnzahlChanged') tiereAnzahlChanged = new EventEmitter<number>();
    @Output('alleTiereHinzufuegen') alleTiereHinzufuegen = new EventEmitter<void>();
    @Output('listeAbschliessen') listeAbschliessen = new EventEmitter<void>();

    constructor(
        private abgangService: GruppeAbgangService,
        private api: KieneBackendApiService,
        private dialog: MatDialog,
        private appService: AppService,
        private messageService: MessageService,
        private gruppenService: GruppenService,
        @Inject(API_BASE_URL_SERVICE) private apiBaseUrl: string,
        public lss: LocalStorageService,
        @Inject(MatDialogRef) @Optional() private dialogRef?: MatDialogRef<any>,
        @Optional()
        @Inject(LISTE_ABSCHLIESSEN_STEPPER_HANDLER)
        private readonly listeAbschliessenStepperHandler?: ListeAbschliessenStepperHandler,
    ) {}

    ngOnInit(): void {
        if (!this.tierliste) {
            this.tierliste = this.abgangService.tierliste;
            if (this.tierliste) {
                this.ladeTiereFuerAbgangsliste('ngOnInit AbgangListeComponent');
                this.ladeAnzahlTiereZurListeHinzufuegbar();
            }
        }

        // Suchfeld-Funktionalität
        this.suchfeldCtrl.valueChanges.subscribe((suchtext: string) => {
            this.filtereTiere(suchtext);
        });

        // Initial alle Tiere anzeigen
        this.gefilterteTiere = [...this.tiere];

        this.abgangService.watchAbgangsliste().subscribe({
            next: liste => {
                if (liste.tierliste) {
                    this.tierliste = liste.tierliste;
                    this.ladeAnzahlTiereZurListeHinzufuegbar();
                    if (liste.reloadGruppenTiere) {
                        this.ladeTiereFuerAbgangsliste('watchAbgangsliste() next');
                    }
                } else {
                    this.tierliste = undefined;
                    this.tiere = [];
                    this.anzahlTiereZurListeHinzufuegbar = null;
                }
            },
        });
    }

    ngOnChanges() {}

    ausListeEntfernen(tier: GruppeTier) {
        const input = new EnterValueInput();
        input.title = 'Tier aus Liste entfernen?';
        input.text = 'Es kann optional ein Grund für das Entfernen eingetragen werden.';
        input.okButton = 'entfernen';
        input.buttonColor = 'accent';
        input.addInputValue('Grund (optional)', 'text');

        const dialogRef = this.dialog.open(EnterValueDialogComponent, {
            data: input,
            position: { top: '10px' },
            disableClose: true,
        });
        dialogRef.afterClosed().subscribe({
            next: (res: EnterValueResponse) => {
                if (res?.result === true) {
                    if (KieneUtils.isNNoU(res.getValue(0))) {
                        tier.grund_entfernung = res.getValue(0);
                    }
                    this.api.post(this.apiBaseUrl + 'gruppe_tier/delete_tierliste.php', tier, undefined).subscribe({
                        next: () => {
                            this.messageService.infoMessage('Das Tier wurde erfolgreich entfernt!');
                            const updatedListe = {
                                ...this.tierliste,
                                anzahl_tiere: Math.max(0, Number(this.tierliste?.anzahl_tiere ?? 1) - 1),
                            };
                            this.abgangService.setAbgangsliste(updatedListe, true, false, true, tier);
                            this.ladeTiereFuerAbgangsliste('ausListeEntfernen() afterClosed');
                        },
                        error: err => {
                            this.messageService.errorMessage('Fehler!', err);
                        },
                    });
                }
            },
        });
    }

    abgangslisteComparator(l1: Tierliste, l2: Tierliste) {
        return l1?.tierliste_id === l2?.tierliste_id;
    }

    abgangslisteLeeren() {
        const input = new ConfirmDialogInput();
        input.headline = 'Liste leeren?';
        input.text = 'Wollen Sie die aktuelle Ausstallliste wirklich leeren?';
        input.okButton = 'Leeren';
        input.buttonColor = 'warn';

        const dialogRef = this.dialog.open(ConfirmDialogComponent, { data: input, disableClose: true });
        dialogRef.afterClosed().subscribe({
            next: (result: ConfirmDialogResult) => {
                if (result?.confirmed) {
                    this.appService.setBlockOnLoading(true);
                    this.api
                        .post(this.apiBaseUrl + 'gruppe_tier/delete_tierliste.php', this.tierliste, undefined)
                        .subscribe({
                            next: () => {
                                this.appService.setBlockOnLoading(false);
                                this.messageService.infoMessage('Die Ausstallliste wurde erfolgreich geleert!');
                                const clearedListe: Tierliste = { ...this.tierliste, anzahl_tiere: 0 };
                                this.tierliste = clearedListe;
                                this.abgangService.setAbgangsliste(clearedListe, true, true);
                            },
                            error: err => {
                                this.appService.setBlockOnLoading(false);
                                this.messageService.errorMessage('Fehler!', err);
                            },
                        });
                }
            },
        });
    }

    abgangslisteSchliessen() {
        if (!this.tierliste?.tierliste_id) {
            return;
        }

        if (this.listeAbschliessenStepperHandler) {
            this.listeAbschliessenStepperHandler.open(this.tierliste).subscribe({
                next: saved => {
                    if (!saved) {
                        return;
                    }
                    this.abgangService.setAbgangsliste(this.tierliste, false, true);
                    this.ladeTiereFuerAbgangsliste('abgangslisteSchliessen() stepper');
                    this.abgangService.tierListeAbgeschlossen(this.tierliste, 'abgangslisteSchliessen');
                    if (this.alsDialog && this.dialogRef) {
                        this.dialogRef.close(this.tierliste);
                    }
                },
            });
            return;
        }

        const dialogRef = this.dialog.open(AbgangslisteSchliessenDialogComponent, {
            maxWidth: '95vw',
            maxHeight: '95vh',
            data: { tierliste: this.tierliste, alsDialog: this.alsDialog },
            disableClose: true,
        });
        dialogRef.afterClosed().subscribe({
            next: result => {
                if (result) {
                    this.abgangService.setAbgangsliste(this.tierliste, false, true);
                    this.ladeTiereFuerAbgangsliste('abgangslisteSchliessen() afterClosed');
                    this.abgangService.tierListeAbgeschlossen(this.tierliste, 'abgangslisteSchliessen');

                    if (this.alsDialog && this.dialogRef) {
                        this.dialogRef.close(this.tierliste);
                    }
                }
            },
        });
    }

    public ladeTiereFuerAbgangsliste(sender: string) {
        console.log('ladeTiereFuerAbgangsliste(): ', sender);
        if (this.tierliste) {
            this.isLoading = true;
            let params = new HttpParams().set('tierliste_id', this.tierliste.tierliste_id);
            params = params.set('orderby', 'editiert desc');

            this.ladeTiereFuerAbgangslisteSub?.unsubscribe();
            this.ladeTiereFuerAbgangslisteSub = this.api
                .getAll(this.apiBaseUrl + 'gruppe_tier/read_scan_kundenportal.php', params)
                .subscribe({
                    next: response => {
                        this.tiere = response.records;
                        this.filtereTiere(this.suchfeldCtrl.value); // Aktualisiere gefilterte Liste
                        this.isLoading = false;
                        this.tiereAnzahlChanged.emit(this.tiere.length); // Emittiere die Anzahl der Tiere
                    },
                    error: err => {
                        this.messageService.errorMessage('Fehler!', err);
                        this.isLoading = false;
                    },
                });
        }
    }

    getTierreklamationFormatted(t: GruppeTier) {
        return t.reklamationen.map(x => x.reklamation).join(', ');
    }

    hasReklamation(tier: GruppeTier) {
        return tier.reklamationen && tier.reklamationen.length > 0;
    }

    istMeldenMoeglich(): boolean {
        const meldenMoeglich = false;
        for (const t of this.tiere) {
            if (KieneUtils.apiStatusId(t.status_id_abgang, 140)) {
                return true;
            }
        }
        return meldenMoeglich;
    }

    downloadAbgangsliste() {
        this.gruppenService.openGruppeAbgangDownloadDialog({ tierliste: this.tierliste });
    }

    /**
     * Filtert die Tiere basierend auf dem Suchtext (Ohrmarke)
     */
    private filtereTiere(suchtext: string): void {
        if (!suchtext || suchtext.trim() === '') {
            // Wenn kein Suchtext, zeige alle Tiere
            this.gefilterteTiere = [...this.tiere];
        } else {
            // Filtere Tiere die den Suchtext in der Ohrmarke enthalten
            const suchtextLower = suchtext.toLowerCase().trim();
            this.gefilterteTiere = this.tiere.filter(
                tier => tier.ohrmarke && tier.ohrmarke.toLowerCase().includes(suchtextLower),
            );
        }
    }

    private ladeAnzahlTiereZurListeHinzufuegbar(): void {
        const gruppeId = Number(this.tierliste?.gruppe_id_abgang || 0);
        if (gruppeId <= 0 || Number(this.tierliste?.mehrere_gruppen) > 0) {
            this.anzahlTiereZurListeHinzufuegbar = null;
            return;
        }
        this.ladeAnzahlTiereZurListeSub?.unsubscribe();
        const params = new HttpParams()
            .set('gruppe_id', String(gruppeId))
            .set('lebendig_nicht_abgegangen', '1')
            .set('limit', '1')
            .set('offset', '0');
        this.ladeAnzahlTiereZurListeSub = this.api
            .getAll(this.apiBaseUrl + 'gruppe_tier/read_scan_kundenportal.php', params)
            .subscribe({
                next: response => {
                    this.anzahlTiereZurListeHinzufuegbar = Number(response?.count ?? 0);
                },
                error: () => {
                    this.anzahlTiereZurListeHinzufuegbar = null;
                },
            });
    }

    alleTiereZuAbgangslisteHinzufuegen(): void {
        const verfuegbar = this.anzahlTiereZurListeHinzufuegbar ?? 0;
        if (!this.tierliste || verfuegbar === 0 || KieneUtils.isApiOne(this.tierliste.mehrere_gruppen)) {
            return;
        }

        const params = new HttpParams()
            .set('gruppe_id', this.tierliste.gruppe_id_abgang.toString())
            .set('lebendig_nicht_abgegangen', '1');
        this.api.getAll(this.apiBaseUrl + 'gruppe_tier/read_scan_kundenportal.php', params).subscribe({
            next: response => {
                const tiereNichtInListe = response.records || [];

                if (tiereNichtInListe.length === 0) {
                    this.messageService.infoMessage('Alle verfügbaren Tiere sind bereits in der Liste!');
                    return;
                }

                const obj = {
                    tierliste_id: this.tierliste.tierliste_id,
                    gruppen_tiere: tiereNichtInListe,
                    gruppe_id: this.tierliste?.gruppe_id_abgang ?? undefined,
                };
                this.api.post(this.apiBaseUrl + 'tierliste/abgang_kundenportal.php', obj, undefined).subscribe({
                    next: (tierlisteAntwort: Tierliste) => {
                        this.anzahlTiereZurListeHinzufuegbar = 0;
                        this.messageService.infoMessage(
                            tiereNichtInListe.length + ' Tiere wurden zur aktuellen Ausstallliste hinzugefügt.',
                        );
                        this.abgangService.setAbgangsliste(tierlisteAntwort, true, true);
                        // Gruppentier-Liste auf der Seite neu laden, von der aus der Dialog geöffnet wurde
                        if (this.tierliste?.gruppe_id_abgang) {
                            this.abgangService.gruppeAktualisieren(this.tierliste.gruppe_id_abgang);
                        }
                    },
                    error: err => {
                        this.messageService.errorMessage('Fehler beim Hinzufügen der Tiere!', err);
                    },
                });
            },
            error: err => {
                this.messageService.errorMessage('Fehler beim Laden der Tiere!', err);
            },
        });
    }

}
